import { makeAutoObservable, runInAction } from 'mobx';
import { forumApi } from '../api/forum_api';
import type {
  ForumSection,
  ForumSectionWithActivity,
  ForumPost,
  ForumComment,
  ForumActivity,
} from '../types';

export class ForumStore {
  // Sections
  sections: ForumSection[] = [];
  sectionsLoading = false;
  sectionsError: string | null = null;

  // Sections with recent activity (for home page)
  sectionsWithActivity: ForumSectionWithActivity[] = [];
  sectionsWithActivityLoading = false;
  sectionsWithActivityError: string | null = null;

  // Posts by section slug
  postsBySectionSlug: Map<string, ForumPost[]> = new Map();
  postsCursorBySectionSlug: Map<string, string | null> = new Map();
  postsHasMoreBySectionSlug: Map<string, boolean> = new Map();
  postsLoading = false;
  postsError: string | null = null;

  // Current post and its comments
  currentPost: ForumPost | null = null;
  currentPostComments: ForumComment[] = [];
  commentsLoading = false;
  commentsCursor: string | null = null;
  commentsHasMore = false;
  commentsError: string | null = null;

  // Recent activity
  recentActivity: ForumActivity[] = [];
  activityLoading = false;
  activityError: string | null = null;

  // Form states
  saving = false;
  saveError: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // ===========================================================================
  // SECTIONS
  // ===========================================================================

  async fetchSections(): Promise<void> {
    if (this.sectionsLoading) return;

    runInAction(() => {
      this.sectionsLoading = true;
      this.sectionsError = null;
    });

    try {
      const response = await forumApi.listSections();
      runInAction(() => {
        this.sections = response.items;
        this.sectionsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.sectionsLoading = false;
        this.sectionsError =
          err instanceof Error ? err.message : 'Failed to load sections';
      });
    }
  }

  getSectionBySlug(slug: string): ForumSection | undefined {
    return this.sections.find((s) => s.slug === slug);
  }

  async createSection(data: {
    title: string;
    description?: string;
    order?: number;
  }): Promise<ForumSection | null> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      const section = await forumApi.createSection(data);
      runInAction(() => {
        this.sections.push(section);
        this.sections.sort((a, b) => a.order - b.order);
        this.saving = false;
      });
      return section;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to create section';
      });
      return null;
    }
  }

  async updateSection(
    slug: string,
    data: { title?: string; description?: string; order?: number }
  ): Promise<ForumSection | null> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      const section = await forumApi.updateSection(slug, data);
      runInAction(() => {
        const index = this.sections.findIndex((s) => s.slug === slug);
        if (index !== -1) {
          this.sections[index] = section;
        }
        this.sections.sort((a, b) => a.order - b.order);
        this.saving = false;
      });
      return section;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to update section';
      });
      return null;
    }
  }

  async fetchSectionsWithRecent(limitPerSection?: number): Promise<void> {
    if (this.sectionsWithActivityLoading) return;

    runInAction(() => {
      this.sectionsWithActivityLoading = true;
      this.sectionsWithActivityError = null;
    });

    try {
      const response = await forumApi.getSectionsWithRecent(limitPerSection);
      runInAction(() => {
        this.sectionsWithActivity = response.items;
        this.sectionsWithActivityLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.sectionsWithActivityLoading = false;
        this.sectionsWithActivityError =
          err instanceof Error ? err.message : 'Failed to load sections with activity';
      });
    }
  }

  // ===========================================================================
  // POSTS
  // ===========================================================================

  async fetchPosts(
    sectionSlug: string,
    loadMore = false
  ): Promise<void> {
    if (this.postsLoading) return;

    const cursor = loadMore
      ? this.postsCursorBySectionSlug.get(sectionSlug)
      : undefined;

    runInAction(() => {
      this.postsLoading = true;
      this.postsError = null;
    });

    try {
      const response = await forumApi.listPosts(sectionSlug, cursor || undefined);
      runInAction(() => {
        if (loadMore) {
          const existing = this.postsBySectionSlug.get(sectionSlug) || [];
          this.postsBySectionSlug.set(sectionSlug, [
            ...existing,
            ...response.items,
          ]);
        } else {
          this.postsBySectionSlug.set(sectionSlug, response.items);
        }
        this.postsCursorBySectionSlug.set(sectionSlug, response.cursor);
        this.postsHasMoreBySectionSlug.set(sectionSlug, response.has_more);
        this.postsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.postsLoading = false;
        this.postsError =
          err instanceof Error ? err.message : 'Failed to load posts';
      });
    }
  }

  getPostsForSection(sectionSlug: string): ForumPost[] {
    const posts = this.postsBySectionSlug.get(sectionSlug) || [];
    return posts.filter((p) => !p.archived);
  }

  hasMorePosts(sectionSlug: string): boolean {
    return this.postsHasMoreBySectionSlug.get(sectionSlug) || false;
  }

  async fetchPost(sectionSlug: string, postSlug: string): Promise<void> {
    runInAction(() => {
      this.postsLoading = true;
      this.postsError = null;
    });

    try {
      const post = await forumApi.getPost(sectionSlug, postSlug);
      runInAction(() => {
        this.currentPost = post;
        this.postsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.postsLoading = false;
        this.postsError =
          err instanceof Error ? err.message : 'Failed to load post';
      });
    }
  }

  async createPost(
    sectionSlug: string,
    data: { title: string; body: string; sticky?: boolean; label?: string }
  ): Promise<ForumPost | null> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      const post = await forumApi.createPost(sectionSlug, data);
      runInAction(() => {
        // Add to the beginning of the posts list for this section
        const existing = this.postsBySectionSlug.get(sectionSlug) || [];
        // If sticky, add at the beginning; otherwise after sticky posts
        if (post.sticky) {
          this.postsBySectionSlug.set(sectionSlug, [post, ...existing]);
        } else {
          const stickyPosts = existing.filter((p) => p.sticky);
          const normalPosts = existing.filter((p) => !p.sticky);
          this.postsBySectionSlug.set(sectionSlug, [
            ...stickyPosts,
            post,
            ...normalPosts,
          ]);
        }
        this.saving = false;
      });
      return post;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to create post';
      });
      return null;
    }
  }

  async updatePost(
    sectionSlug: string,
    postSlug: string,
    data: {
      title?: string;
      body?: string;
      sticky?: boolean;
      label?: string;
      archived?: boolean;
    }
  ): Promise<ForumPost | null> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      const post = await forumApi.updatePost(sectionSlug, postSlug, data);
      runInAction(() => {
        // Update in posts list
        const posts = this.postsBySectionSlug.get(sectionSlug) || [];
        const index = posts.findIndex((p) => p.slug === postSlug);
        if (index !== -1) {
          posts[index] = post;
        }
        // Update current post if it's the same
        if (this.currentPost?.slug === postSlug) {
          this.currentPost = post;
        }
        this.saving = false;
      });
      return post;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to update post';
      });
      return null;
    }
  }

  // ===========================================================================
  // COMMENTS
  // ===========================================================================

  async fetchComments(
    sectionSlug: string,
    postSlug: string,
    loadMore = false
  ): Promise<void> {
    if (this.commentsLoading) return;

    const cursor = loadMore ? this.commentsCursor : undefined;

    runInAction(() => {
      this.commentsLoading = true;
      this.commentsError = null;
    });

    try {
      const response = await forumApi.listComments(
        sectionSlug,
        postSlug,
        cursor || undefined
      );
      runInAction(() => {
        if (loadMore) {
          this.currentPostComments = [
            ...this.currentPostComments,
            ...response.items,
          ];
        } else {
          this.currentPostComments = response.items;
        }
        this.commentsCursor = response.cursor;
        this.commentsHasMore = response.has_more;
        this.commentsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.commentsLoading = false;
        this.commentsError =
          err instanceof Error ? err.message : 'Failed to load comments';
      });
    }
  }

  async createComment(
    sectionSlug: string,
    postSlug: string,
    body: string
  ): Promise<ForumComment | null> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      const comment = await forumApi.createComment(sectionSlug, postSlug, body);
      runInAction(() => {
        this.currentPostComments.push(comment);
        // Update comment count on current post
        if (this.currentPost) {
          this.currentPost = {
            ...this.currentPost,
            comment_count: this.currentPost.comment_count + 1,
          };
        }
        this.saving = false;
      });
      return comment;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to create comment';
      });
      return null;
    }
  }

  async updateComment(
    sectionSlug: string,
    postSlug: string,
    commentSlug: string,
    data: { body?: string; archived?: boolean }
  ): Promise<ForumComment | null> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      const comment = await forumApi.updateComment(
        sectionSlug,
        postSlug,
        commentSlug,
        data
      );
      runInAction(() => {
        const index = this.currentPostComments.findIndex(
          (c) => c.slug === commentSlug
        );
        if (index !== -1) {
          this.currentPostComments[index] = comment;
        }
        this.saving = false;
      });
      return comment;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to update comment';
      });
      return null;
    }
  }

  async toggleLike(
    sectionSlug: string,
    postSlug: string,
    commentSlug: string
  ): Promise<void> {
    try {
      const updatedComment = await forumApi.toggleLike(
        sectionSlug,
        postSlug,
        commentSlug
      );
      runInAction(() => {
        const index = this.currentPostComments.findIndex(
          (c) => c.slug === commentSlug
        );
        if (index !== -1) {
          this.currentPostComments[index] = updatedComment;
        }
      });
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  }

  // ===========================================================================
  // ACTIVITY
  // ===========================================================================

  async fetchRecentActivity(limit?: number): Promise<void> {
    if (this.activityLoading) return;

    runInAction(() => {
      this.activityLoading = true;
      this.activityError = null;
    });

    try {
      const response = await forumApi.getRecentActivity(limit);
      runInAction(() => {
        this.recentActivity = response.items.filter((item) => !item.archived);
        this.activityLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.activityLoading = false;
        this.activityError =
          err instanceof Error ? err.message : 'Failed to load activity';
      });
    }
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  clearCurrentPost(): void {
    this.currentPost = null;
    this.currentPostComments = [];
    this.commentsCursor = null;
    this.commentsHasMore = false;
  }

  clearErrors(): void {
    this.sectionsError = null;
    this.postsError = null;
    this.commentsError = null;
    this.activityError = null;
    this.saveError = null;
  }
}

export const forumStore = new ForumStore();
